import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ChancenKurveEventlog } from '@chancenwerk/db/eventlogs/ChancenKurveEventlog.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { DateTime } from 'luxon';

const getLastChancenKurveFinish = registerQuery({
  type: () => DateTime,
  operationName: 'getLastChancenKurveFinish',
  resolve: (lessonID: string, hash: string) => {
    // ! We use the hash to avoid caching issues
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const col = await getRawCollection<ChancenKurveEventlog>(ChancenKurveEventlog._collectionName);
      const res = await col.findOne(
        { _class: ChancenKurveEventlog._class, lessonID, userID: user._id },
        { sort: { createdAt: -1 }, limit: 1 },
      );

      if (!res) {
        return DateTime.now().minus({ years: 10 });
      }
      return DateTime.fromJSDate(res?.createdAt ?? new Date());
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'lessonID' },
    { type: () => String, name: 'hash' },
  ],
});

const commitLastChanceKurveFinish = registerMutation({
  type: () => DateTime,
  operationName: 'commitLastChanceKurveFinish',
  resolve: (lessonID: string) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const eventlog = new ChancenKurveEventlog({
        lessonID,
        userID: user._id,
        createdAt: new Date(),
      });
      await eventlog.create();

      return DateTime.fromJSDate(eventlog.createdAt);
    };
  },
  options: {},
  params: [{ type: () => String, name: 'lessonID' }],
});

export namespace chancenKurveAPI {
  export const lastFinish = getLastChancenKurveFinish;
  export const commitFinish = commitLastChanceKurveFinish;
}
